import { memo, useCallback, useState } from 'react';
import {
  MaterialRequisitionDocumentRequest,
  MaterialRequisitionDocumentResponse,
} from '@Types/api/form';
import { fileUploadActions } from '@Store/slices/fileUploadSlice';
import { RcFile } from 'antd/es/upload';
import { generateFilepath } from '@Utils/generateFilepath';
import { RcCustomRequestOptions } from '@Components/types/UploadType';
import { currentFileUploadSelector } from '@Store/selectors/fileUploadSelector';
import { useAppDispatch, useAppSelector } from '@Hooks/index';
import { FormikHelpers, useFormik } from 'formik';
import { FileUpload, User } from '@Types/index';
import { Attachment } from '@Components/types/Attachment';
import { useFormListMutation } from '@Store/api/dmsApiSlice';

import { getDocument } from '@Features/utils/document';
import { materialRequestActions } from '../../../store/materialRequestSlice';
import DocumentModalContent from './components/DocumentModalContent/DocumentModalContent';
import MaterialAddModal from '../Base/MaterialAddModal/MaterialAddModal';
import {
  currentMaterialRequest,
  isButtonDisable,
  modalManager,
} from '../../../store/selectors/materialRequestSelector';

import { initialValuesForm } from './initialValues';

import DocumentListItems from './components/DocumentListItems/DocumentListItems';
import MaterialEmptyScreen from '../Base/MaterialEmptyScreen/MaterialEmptyScreen';

import { getDocumentFolder } from '../../utils/document';

type handleSubmitType = (
  values: MaterialRequisitionDocumentRequest,
  formikHelpers: FormikHelpers<MaterialRequisitionDocumentRequest>
) => void;

interface DocumentsSectionProps {
  listItems: Array<MaterialRequisitionDocumentResponse>;
  currentUser: User;
  handleSaveDocument: (
    formikValues: MaterialRequisitionDocumentRequest,
    requisitionId: number,
    fileUpload: FileUpload,
    addAnotherItem?: boolean
  ) => Promise<void>;
}

const DocumentsSection: React.FC<DocumentsSectionProps> = ({
  listItems,
  currentUser,
  handleSaveDocument,
}) => {
  const dispatch = useAppDispatch();
  const modalManagerSetting = useAppSelector(modalManager);
  const { open, title, okText, isEditMode } = modalManagerSetting;
  const stateButton = useAppSelector(isButtonDisable);
  const currentMRequest = useAppSelector(currentMaterialRequest);
  const currentFileUpload = useAppSelector(currentFileUploadSelector);
  const [getFormList] = useFormListMutation();
  const handleSubmitForm: handleSubmitType = (): void => {};
  const [uploadedFile, setUploadedFile] = useState<Attachment | undefined>(
    undefined
  );

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    onSubmit: handleSubmitForm,
  });

  const handleDisableSaveButton = useCallback(
    (isDisable: boolean): void => {
      dispatch(materialRequestActions.setModalButtonDisable(isDisable));
    },
    [modalManagerSetting]
  );

  const clearAll = (): void => {
    dispatch(
      materialRequestActions.setModalManager({
        isEditMode: false,
        okText: '',
        title: '',
        open: false,
      })
    );
    dispatch(fileUploadActions.setFile(null));
    handleDisableSaveButton(true);
    formik.resetForm();
  };

  const handleCloseModal = (): void => {
    clearAll();
  };

  const onFetchDocumentFile = useCallback(
    (requestDocumentId: string | number, fileName: string) => {
      setUploadedFile(undefined);
      dispatch(fileUploadActions.setFile(null));

      if (!requestDocumentId || !fileName) {
        return;
      }

      const documentFolderPath = getDocumentFolder(requestDocumentId);

      getFormList(documentFolderPath)
        .unwrap()
        .then((response) => {
          const matchedDocument = getDocument(response, fileName);

          if (matchedDocument) {
            setUploadedFile({
              id: matchedDocument.documentId,
              name: fileName,
            });
          }
        });
    },
    []
  );

  const handleEditMaterialItem = (
    documentItem: MaterialRequisitionDocumentResponse
  ): void => {
    if (documentItem) {
      onFetchDocumentFile(documentItem.reqId!, documentItem.filePath!);
    }

    formik.setValues({
      ...documentItem,
    });

    dispatch(
      materialRequestActions.setModalManager({
        isEditMode: true,
        okText: 'Save Changes',
        title: 'Edit Document',
        open: true,
      })
    );

    handleDisableSaveButton(false);
  };

  const handleFileRemove = useCallback((): void => {
    dispatch(fileUploadActions.setFile(null));
  }, []);

  const handleFileRequest = useCallback(
    (options: RcCustomRequestOptions): void => {
      const { file, onSuccess, onError } = options;
      const cb = (): void => {};
      const successCb = onSuccess || cb;
      const errorCb = onError || cb;
      const formData = new FormData();
      formData.append('file', file);

      if (file) {
        const filepath = generateFilepath(
          'MR',
          (file as RcFile).name,
          currentUser?.empNumber
        );
        dispatch(
          fileUploadActions.setFile({
            uploadedFile: file as RcFile,
            formData,
            filepath,
          })
        );
        successCb('Ok');
      } else {
        errorCb(new Error('file not uploaded'));
      }
    },
    [currentUser]
  );

  const handleOkModal = useCallback((): void => {
    const formikValues = {
      ...formik.values,
    };

    handleSaveDocument(
      formikValues,
      currentMRequest?.requestId,
      currentFileUpload
    );
    clearAll();
  }, [formik.values, currentMRequest?.requestId, currentFileUpload]);

  const handleDownload = (url: string | undefined, filePath: string): void => {
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = filePath;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const handleDeleteItem = useCallback(
    (document: MaterialRequisitionDocumentResponse, documentName: string) => {
      dispatch(
        materialRequestActions.setModalManagerItem({
          deleteItem: [document],
          modalType: 'Document',
          titleModal: 'Delete Document',
          messageModal: `Are you sure you want to delete document ${documentName}?`,
          showModal: true,
        })
      );
    },
    [dispatch]
  );

  return (
    <>
      {listItems?.length ? (
        <DocumentListItems
          onEditDocumentItem={handleEditMaterialItem}
          onDeleteItem={handleDeleteItem}
          onDownload={handleDownload}
          listItems={listItems}
        />
      ) : (
        <MaterialEmptyScreen text="Please Add Documents To Proceed" />
      )}
      <MaterialAddModal
        ModalContent={
          <DocumentModalContent
            isEditMode={isEditMode}
            isDisableSaveButton={stateButton}
            formik={formik}
            uploadedFile={uploadedFile}
            onSave={handleSaveDocument}
            onFileRequest={handleFileRequest}
            onFileRemove={handleFileRemove}
            onDisableSaveButton={handleDisableSaveButton}
          />
        }
        okText={okText}
        title={title}
        handleCancel={handleCloseModal}
        handleOk={handleOkModal}
        modalOpen={open}
        isDisableSaveButton={stateButton}
      />
    </>
  );
};

export default memo(DocumentsSection);
